// components.jsx — 万物 TV components.
var { useState, useEffect, useRef, useMemo, useCallback } = React;  // 5/25 v10.74: var — multi .jsx 共 scope const 撞

/* ============================================================ */
/* HOOKS                                                        */
/* ============================================================ */

// Simulated playback clock. Auto-plays. Returns {time, playing, set, toggle, speed, setSpeed}
function usePlayback(duration, initialTime = 152) {
  const [time, setTime] = useState(initialTime);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1.0);
  const rafRef = useRef(0);
  const lastRef = useRef(performance.now());

  useEffect(() => {
    function tick(now) {
      const dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      if (playing) {
        setTime((t) => {
          const next = t + dt * speed;
          return next >= duration ? 0 : next;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    lastRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, speed, duration]);

  return {
    time, setTime,
    playing, setPlaying,
    toggle: () => setPlaying((p) => !p),
    speed, setSpeed,
  };
}

function fmt(t) {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/* ============================================================ */
/* HERO + STATUS BAR (#4)                                       */
/* ============================================================ */

function Hero({ tweaks, avatar, activeTab, setActiveTab }) {
  const [uptime, setUptime] = useState({ days: 187, hours: 14, mins: 32 });
  const [viewers, setViewers] = useState(avatar?.viewers || 2143);

  // 5/18 §2.6: 真值 viewers 从 GPU `/tv/viewers` 拉 (lipsync_daemon 实时 active_ws + baseline).
  // 仅 xiaocheng slug 走 GPU 真接 (其他 shop slug 还没 GPU 计数, 保留 mock ticker).
  useEffect(() => {
    if (avatar?.slug !== "xiaocheng" && avatar?.slug) {
      // 非 xiaocheng — 老 ticker (本店 viewers mock)
      const id = setInterval(() => {
        setViewers((v) => Math.max(0, v + Math.round((Math.random() - 0.5) * 14)));
      }, 3000);
      return () => clearInterval(id);
    }
    // xiaocheng — GPU 真值, 5s 轮询
    let cancelled = false;
    const pollViewers = async () => {
      try {
        const r = await fetch('https://radio.wanwushop.cn/tv/viewers?t=' + Date.now(),
                              { cache: 'no-store' });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const j = await r.json();
        if (!cancelled && typeof j.online === 'number') setViewers(j.online);
      } catch (e) { /* keep last value */ }
    };
    pollViewers();
    const id = setInterval(pollViewers, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [avatar?.slug]);

  const isLive = avatar?.status === "LIVE" || avatar?.status === "CHAT" || !avatar;
  const country = avatar ? window.COUNTRIES?.[avatar.country] : null;
  const cat = avatar ? window.CATEGORIES?.find(c => c.id === avatar.cat) : null;
  const langs = avatar?.langs || ["普通话", "English"];

  // 5/18 r4: shop-NNN 仅显 [互动] tab — 这些数字店长只有真互动 (WS 真 persona + 真 motion mp4),
  //          没有 24/7 HLS / 节目库 / 主题订阅.
  // 5/18 v9.4 P0: xiaocheng + mode=interact 也仅显 [互动] tab + "回真直播" 跳 xiaocheng.html.
  //          老 4 tab 含 mock 节目单 + mock 字幕 (1869 黑色星期五 全编造) 误导 founder, hide.
  const isShopAvatar = avatar?.slug && /^shop-\d+$/.test(avatar.slug);
  const _qMode = (typeof window !== "undefined")
    ? new URL(window.location.href).searchParams.get("mode")
    : null;
  const isXcInteract = avatar?.slug === "xiaocheng" && _qMode === "interact";
  const tabs = (isShopAvatar || isXcInteract) ? [
    { id: "interact", label: "互动",     icon: "message-circle-more", dot: true },
  ] : [
    { id: "live",     label: "正在直播", icon: "radio" },
    { id: "library",  label: "节目库",   icon: "library" },
    { id: "subs",     label: "主题订阅", icon: "bell" },
    { id: "interact", label: "互动",     icon: "message-circle-more", dot: true },
  ];

  return (
    <header className="hero-region">
      <div className="hero">
        <div className="hero-brand">
          <a href="/" className="hero-back" aria-label="返回市场" title="返回数字人市场"
             onClick={(e) => { e.preventDefault(); window.location.assign("/"); }}>
            <i data-lucide="arrow-left"></i>
          </a>
          <div className="hero-mark" aria-hidden="true">
            <img src="assets/wanwu-deer.png" alt="" className="hero-mark-img" />
          </div>
          <div className="hero-divider" aria-hidden="true"></div>
          <div className="hero-text">
            <div className="hero-title">
              <span className="hero-title-zh">{avatar?.name || "万物 TV"}</span>
              <span className="hero-title-en">EVERYTHING TV</span>
            </div>
            <div className="hero-sub">
              {isShopAvatar ? (
                <>
                  <span>AI 数字店长</span>
                  <span className="hero-sub-sep">·</span>
                  <span>{avatar?.tags?.[0] || avatar?.blurb || "店主"}</span>
                  <span className="hero-sub-sep">·</span>
                  <span>真人嘴动 + 即聊即答</span>
                </>
              ) : (
                <>
                  <span>永不消逝的电磁波</span>
                  <span className="hero-sub-sep">·</span>
                  <span>主播 {avatar?.name || "小橙"}</span>
                  <span className="hero-sub-sep">·</span>
                  <span>24/7 实时视频播报</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="hero-status">
          {tweaks.showLiveBadge && isLive && (
            <div className="chip chip-live">
              <span style={{ fontWeight: 600, letterSpacing: "0.08em" }}>LIVE</span>
            </div>
          )}
          <div className="chip">
            <i data-lucide="users"></i>
            <span className="label">在线</span>
            <strong>{viewers.toLocaleString()}</strong>
          </div>
          <div className="chip chip-host">
            <span className="avatar">{(avatar?.name || "小橙").slice(-1)}</span>
            <span className="label">{isShopAvatar ? "店主" : "主播"}</span>
            <strong>{avatar?.name || "小橙"}{country ? ` ${country.flag}` : ""}</strong>
          </div>
          {isShopAvatar ? (
            <div className="chip">
              <i data-lucide="store"></i>
              <span className="label">类目</span>
              <strong>{cat?.zh || avatar?.cat || "店"}</strong>
            </div>
          ) : (
            <div className="chip">
              <i data-lucide="timer"></i>
              <span className="label">连续播报</span>
              <strong>{uptime.days}<span style={{opacity:0.55, margin:"0 2px"}}>天</span>{uptime.hours}<span style={{opacity:0.55, margin:"0 2px"}}>时</span></strong>
            </div>
          )}
          <div className="chip">
            <i data-lucide="languages"></i>
            <span className="label">语言</span>
            <strong>{langs[0]}{langs.length > 1 ? ` · +${langs.length - 1}` : ""}</strong>
          </div>
        </div>
      </div>

      <nav className="nav-tabs" aria-label="主导航">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`tab ${activeTab === t.id ? "active" : ""}`}
            onClick={() => {
              // 5/18 §2.4 fix: avatar.html 的 live tab 用 mock CURRENT_SEGMENT (商业财富史 1869),
              // 真 24/7 HLS 在 xiaocheng.html. 点 "正在直播" 时 slug=xiaocheng 必跳过去.
              if (t.id === "live" && (avatar?.slug === "xiaocheng" || !avatar?.slug)) {
                window.location.href = "xiaocheng.html";
                return;
              }
              setActiveTab(t.id);
            }}
          >
            <i data-lucide={t.icon}></i>
            <span>{t.label}</span>
            {t.dot && <span className="tab-dot" aria-label="NEW"></span>}
          </button>
        ))}
      </nav>
    </header>
  );
}

/* ============================================================ */
/* PLAYER (#1)                                                  */
/* ============================================================ */

function Player({ tweaks, playback, avatar }) {
  const seg = window.CURRENT_SEGMENT;
  const { time, setTime, playing, toggle, speed, setSpeed } = playback;

  const [hoverControls, setHoverControls] = useState(false);
  const [muted, setMuted] = useState(false);
  const [vol, setVol] = useState(0.75);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [draggingScrub, setDraggingScrub] = useState(false);
  const scrubRef = useRef(null);

  // current sentence
  const currentSentenceIdx = useMemo(() => {
    return seg.sentences.findIndex((s) => time >= s.start && time < s.end);
  }, [time, seg]);
  const currentSentence = seg.sentences[currentSentenceIdx] || seg.sentences[0];

  // current word within sentence
  const currentWordIdx = useMemo(() => {
    if (!currentSentence) return 0;
    const elapsed = time - currentSentence.start;
    const total = currentSentence.end - currentSentence.start;
    const words = currentSentence.words;
    // weight by char-length so each word gets proportional time
    const lens = words.map((w) => Math.max(1, w.length));
    const sumLens = lens.reduce((a, b) => a + b, 0);
    let acc = 0;
    for (let i = 0; i < words.length; i++) {
      const dur = (lens[i] / sumLens) * total;
      if (elapsed >= acc && elapsed < acc + dur) return i;
      acc += dur;
    }
    return words.length - 1;
  }, [time, currentSentence]);

  // keyboard shortcuts (C = captions, F = fullscreen, Space = play)
  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "c" || e.key === "C") { e.preventDefault(); setCaptionsOn((c) => !c); }
      if (e.key === "f" || e.key === "F") { e.preventDefault(); setFullscreen((f) => !f); }
      if (e.key === " ") { e.preventDefault(); toggle(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);

  // scrubber drag
  function onScrubDown(e) {
    setDraggingScrub(true);
    seekFromEvent(e);
    function onMove(ev) { seekFromEvent(ev); }
    function onUp() {
      setDraggingScrub(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }
  function seekFromEvent(e) {
    const el = scrubRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setTime(ratio * seg.duration);
  }

  const progress = (time / seg.duration) * 100;
  const buffered = Math.min(100, progress + 22);

  function cycleSpeed() {
    const opts = [0.75, 1.0, 1.25, 1.5];
    const i = opts.indexOf(speed);
    setSpeed(opts[(i + 1) % opts.length]);
  }

  return (
    <article className={`player ${fullscreen ? "is-fullscreen" : ""}`}>
      <div
        className="video-wrap"
        onMouseEnter={() => setHoverControls(true)}
        onMouseLeave={() => setHoverControls(false)}
      >
        {/* anchor portrait */}
        <div className="anchor">
          <div className="anchor-figure">
            <AnchorPortrait talking={playing} avatar={avatar} />
          </div>
          <div className="anchor-vignette"></div>
          <div className="anchor-noise"></div>
        </div>

        {/* floating badges */}
        <div className="video-badges">
          <div className="badge badge-live">
            <span className="dot"></span>
            <span>现正播报</span>
          </div>
          <div className="badge badge-topic">
            <span>{seg.topic}</span>
            <span className="sep">·</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.85 }}>{seg.topicEn}</span>
          </div>
        </div>

        {/* bitrate */}
        <div className="bitrate">
          <span className="dot"></span>
          {seg.bitrate} · auto
        </div>

        {/* subtitle bar */}
        {captionsOn && (
          <div className="sub-bar" style={{ "--caption-size": `${tweaks.captionSize}px` }}>
            <div className="sub-line">
              {currentSentence?.words.map((w, i) => (
                <span
                  key={i}
                  className={`sub-word ${i < currentWordIdx ? "spoken" : ""} ${i === currentWordIdx ? "current" : ""}`}
                >
                  {w}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* controls */}
      <div className="controls" aria-label="播放控制">
        <button className="btn-play" onClick={toggle} aria-label={playing ? "暂停" : "播放"}>
          {playing ? (
            <svg viewBox="0 0 16 16"><rect x="4" y="3" width="3" height="10" rx="1" /><rect x="9" y="3" width="3" height="10" rx="1" /></svg>
          ) : (
            <svg viewBox="0 0 16 16"><path d="M5 3 L13 8 L5 13 Z"/></svg>
          )}
        </button>

        <div className="ctl-time">
          <span className="now">{fmt(time)}</span>
          <span className="sep">/</span>
          <span>{fmt(seg.duration)}</span>
        </div>

        <div
          className={`scrubber ${draggingScrub ? "dragging" : ""}`}
          ref={scrubRef}
          onPointerDown={onScrubDown}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={seg.duration}
          aria-valuenow={Math.floor(time)}
          aria-label="播放进度"
          tabIndex={0}
        >
          <div className="scrubber-track">
            <div className="scrubber-buffer" style={{ width: `${buffered}%` }}></div>
            <div className="scrubber-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="scrubber-thumb" style={{ left: `${progress}%` }}></div>
        </div>

        <div className="ctl-right">
          <div className="opt-vol">
            <button
              className="icon-btn"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? "取消静音" : "静音"}
              title={muted ? "取消静音" : "静音"}
            >
              <i data-lucide={muted || vol === 0 ? "volume-x" : vol < 0.5 ? "volume-1" : "volume-2"}></i>
            </button>
            <div className="vol-track" onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setVol(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
              setMuted(false);
            }}>
              <div className="vol-fill" style={{ width: `${(muted ? 0 : vol) * 100}%` }}></div>
            </div>
          </div>

          <button className="opt-speed" onClick={cycleSpeed} title="倍速 (循环 0.75 / 1.0 / 1.25 / 1.5)">
            {speed.toFixed(2).replace(/0$/, "")}×
          </button>

          <button
            className={`icon-btn ${captionsOn ? "active" : ""}`}
            onClick={() => setCaptionsOn((c) => !c)}
            title="字幕开关 (C)"
            aria-pressed={captionsOn}
          >
            <i data-lucide="captions"></i>
          </button>

          <button
            className="icon-btn"
            onClick={() => setFullscreen((f) => !f)}
            title="全屏 (F)"
          >
            <i data-lucide={fullscreen ? "minimize" : "maximize"}></i>
          </button>
        </div>
      </div>
    </article>
  );
}

/* ----- Anchor portrait — motion mp4 (when available) loop, fallback static portrait. ----- */
// 5/18 M4: avatar-aware. Pulls motion mp4 from window.__MOTION_INDEX[slug]
// (loaded by marketplace.jsx OSS index.json fetch), falls back to avatar.portrait
// (jpg from radio.wanwushop.cn/tv/lib/), then assets/xiaocheng.png.
function AnchorPortrait({ talking, avatar }) {
  const slug = avatar?.slug || "xiaocheng";
  const [motionUrl, setMotionUrl] = useState(null);
  const videoRef = useRef(null);

  // Poll window.__MOTION_INDEX in case the marketplace fetch lands after mount.
  useEffect(() => {
    let cancelled = false;
    function pick() {
      if (cancelled) return;
      const idx = window.__MOTION_INDEX || {};
      const u = idx[slug];
      if (u && u !== motionUrl) setMotionUrl(u);
    }
    pick();
    const id = setInterval(pick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug, motionUrl]);

  const portraitJpg = avatar?.portrait
    || (slug === "xiaocheng" ? "assets/xiaocheng.png" : null)
    || "assets/xiaocheng.png";

  return (
    <div className="anchor-face" style={{ position: "relative", width: "100%", height: "100%" }}>
      <div style={{
        position: "absolute", inset: 0,
        borderRadius: "50%",
        background: "radial-gradient(circle at 50% 40%, rgba(255,180,110,0.18) 0%, transparent 65%)",
        filter: "blur(4px)",
      }}></div>
      <div style={{
        position: "absolute", inset: "10% 8% 0 8%",
        borderRadius: "999px 999px 18px 18px",
        overflow: "hidden",
        boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset",
        background: "#1a1410",
      }}>
        {motionUrl ? (
          <video
            ref={videoRef}
            src={motionUrl}
            autoPlay muted loop playsInline preload="auto"
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "50% 22%",
              filter: "saturate(1.05) contrast(1.02)",
            }}
          />
        ) : (
          <img
            src={portraitJpg}
            alt={`数字主播 ${avatar?.name || "小橙"}`}
            style={{
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "50% 22%",
              filter: "saturate(1.05) contrast(1.02)",
            }}
          />
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(255,180,110,0.06) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.18) 100%)",
          pointerEvents: "none",
        }}></div>
        {talking && !motionUrl && (
          <div style={{
            position: "absolute",
            left: "50%", top: "62%",
            transform: "translate(-50%, -50%)",
            width: "12%", height: "3.2%",
            borderRadius: "999px",
            background: "rgba(255,117,43,0.0)",
            boxShadow: "0 0 24px 6px rgba(255,117,43,0.15)",
            animation: "talking 0.42s steps(2, end) infinite",
            pointerEvents: "none",
          }}></div>
        )}
      </div>
    </div>
  );
}

/* ============================================================ */
/* TRANSCRIPT (#2)                                              */
/* ============================================================ */

function Transcript({ tweaks, playback, setTweak }) {
  const seg = window.CURRENT_SEGMENT;
  const { time, setTime } = playback;
  const mode = tweaks.transcriptMode;

  const currentSentenceIdx = useMemo(() => {
    return seg.sentences.findIndex((s) => time >= s.start && time < s.end);
  }, [time, seg]);
  const currentSentence = seg.sentences[currentSentenceIdx] || seg.sentences[0];

  const currentWordIdx = useMemo(() => {
    if (!currentSentence) return 0;
    const elapsed = time - currentSentence.start;
    const total = currentSentence.end - currentSentence.start;
    const lens = currentSentence.words.map((w) => Math.max(1, w.length));
    const sum = lens.reduce((a, b) => a + b, 0);
    let acc = 0;
    for (let i = 0; i < currentSentence.words.length; i++) {
      const dur = (lens[i] / sum) * total;
      if (elapsed >= acc && elapsed < acc + dur) return i;
      acc += dur;
    }
    return currentSentence.words.length - 1;
  }, [time, currentSentence]);

  // auto-scroll the active sentence into view in full mode
  const fullRef = useRef(null);
  useEffect(() => {
    if (mode !== "full") return;
    const root = fullRef.current; if (!root) return;
    const active = root.querySelector(".tx-sentence.current");
    if (active) {
      const r = root.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const targetTop = ar.top - r.top - r.height / 2 + ar.height / 2;
      root.scrollBy({ top: targetTop, behavior: "smooth" });
    }
  }, [currentSentenceIdx, mode]);

  return (
    <section className="transcript" aria-label="字幕 / 转写">
      <div className="tx-head">
        <div className="tx-title">
          <i data-lucide="file-text"></i>
          <span>文字稿</span>
          <span style={{ opacity: 0.5, fontWeight: 400 }}>· transcription</span>
        </div>
        <div className="tx-toggle" role="tablist">
          <button
            role="tab" aria-selected={mode === "current"}
            className={mode === "current" ? "active" : ""}
            onClick={() => setTweak("transcriptMode", "current")}
          >当前句</button>
          <button
            role="tab" aria-selected={mode === "full"}
            className={mode === "full" ? "active" : ""}
            onClick={() => setTweak("transcriptMode", "full")}
          >完整段</button>
        </div>
      </div>

      {mode === "current" ? (
        <div className="tx-current" key={currentSentenceIdx /* re-anim on sentence change */}>
          <div>
            {currentSentence.words.map((w, i) => (
              <span
                key={i}
                className={`word ${i < currentWordIdx ? "spoken" : ""} ${i === currentWordIdx ? "current" : ""}`}
              >{w}</span>
            ))}
          </div>
        </div>
      ) : (
        <div className="tx-full" ref={fullRef}>
          {seg.sentences.map((s, i) => (
            <div
              key={i}
              className={`tx-sentence ${i < currentSentenceIdx ? "spoken" : ""} ${i === currentSentenceIdx ? "current" : ""}`}
              onClick={() => setTime(s.start + 0.1)}
              title={`跳转到 ${fmt(s.start)}`}
            >
              {s.words.join("")}
            </div>
          ))}
        </div>
      )}

      <div className="tx-meta">
        <span className="pair"><strong>主题</strong> {seg.topic}</span>
        <span className="pair"><strong>字数</strong> {seg.wordCount}</span>
        <span className="pair"><strong>预计时长</strong> {fmt(seg.duration)}</span>
        <span className="pair"><strong>来源</strong> <span className="mono">{seg.source}</span></span>
      </div>
    </section>
  );
}

/* ============================================================ */
/* UP NEXT (#3A)                                                */
/* ============================================================ */

function UpNext({ playback }) {
  const upn = window.UP_NEXT;
  const seg = window.CURRENT_SEGMENT;
  const remaining = Math.max(0, seg.duration - playback.time);
  // synthesize a "X 秒后" countdown that follows the live segment's progress
  const seconds = Math.min(upn.countdownSeconds + Math.floor(remaining), 999);

  return (
    <a href="#" className="upnext" onClick={(e) => { e.preventDefault(); playback.setTime(seg.duration - 0.5); }}>
      <div className="thumb" aria-hidden="true">
        <i data-lucide="trending-up"></i>
      </div>
      <div className="upnext-text">
        <div className="upnext-meta">
          <span className="upnext-countdown">
            <i data-lucide="timer"></i>
            {Math.floor(seconds / 60) > 0 ? `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒后` : `${seconds} 秒后`}
          </span>
          <span className="topic-tag">{upn.topic}</span>
        </div>
        <div className="upnext-title">{upn.title}</div>
        <div className="upnext-blurb">{upn.blurb}</div>
      </div>
    </a>
  );
}

/* ============================================================ */
/* 24H TIMELINE (#3B)                                           */
/* ============================================================ */

function Timeline({ playback }) {
  const schedule = window.SCHEDULE_24H;
  const nowHour = window.NOW_HOUR;

  // current local clock for ticker
  const [clock, setClock] = useState(() => {
    const d = new Date();
    return `${String(nowHour).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setClock(`${String(nowHour).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
    }, 30000);
    return () => clearInterval(id);
  }, [nowHour]);

  const listRef = useRef(null);
  // Scroll the current slot into view once on mount.
  useEffect(() => {
    const root = listRef.current; if (!root) return;
    const cur = root.querySelector(".slot.now");
    if (cur) {
      const r = root.getBoundingClientRect();
      const cr = cur.getBoundingClientRect();
      root.scrollBy({ top: cr.top - r.top - 80, behavior: "instant" });
    }
  }, []);

  return (
    <section className="timeline" aria-label="24 小时主题轮转">
      <div className="timeline-head">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <i data-lucide="calendar-clock" style={{ width: 14, height: 14, color: "var(--tv-accent)" }}></i>
          <span style={{ textTransform: "uppercase", letterSpacing: "0.12em", fontSize: 11 }}>24h 节目单</span>
        </span>
        <span className="now-mark">NOW · {clock}</span>
      </div>

      <div className="timeline-list" ref={listRef}>
        {schedule.map((s) => {
          const past = s.hour < nowHour;
          const now = s.hour === nowHour;
          return (
            <div
              key={s.hour}
              className={`slot ${past ? "past" : ""} ${now ? "now" : ""}`}
              onClick={() => {
                if (now) playback.setTime(0);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="slot-time">
                {String(s.hour).padStart(2, "0")}:00
              </div>
              <div className="slot-body">
                <span className="slot-color" style={{ "--slot-color": s.color, background: s.color }}></span>
                <span className="slot-name">{s.name}</span>
                <span className="slot-chips">
                  {s.chips.slice(0, 2).map((c, i) => (
                    <span key={i} className="slot-chip">{c}</span>
                  ))}
                </span>
              </div>
              {now && (
                <div className="slot-tooltip">
                  <div className="slot-tooltip-title">代表段</div>
                  <div>{s.name}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

Object.assign(window, { Hero, Player, Transcript, UpNext, Timeline, usePlayback, fmt });
